import { ReactNode } from 'react'
import {
  RaidPlanAssignmentEntity,
  RaidPlanEncounterAssignmentAbility,
} from 'app/frontend/types/custom/RaidPlan'
import { RaidPlanOwner } from 'app/frontend/base/api/raid_plans'
import { useVisualPlan } from 'app/frontend/base/config/queries'
import VisualPlan from 'app/frontend/types/custom/VisualPlan'
import {
  VisualPlanData,
  VisualPlanScreen,
  VisualRaidPlan,
} from '../components/TeamPlanAssignmentVisualCard'
import { RaidstratsPlayer } from '../components/RaidstratsContainer'
import useVisualPlanLiveSync from '../components/visual_plans/hooks/useVisualPlanLiveSync'
import { resolveVisualPlanEntityAssignments } from '../components/visual_plans/shapes/entityAssignmentMatching'
import {
  getActiveVisualScreens,
  getScopeScreens,
} from '../components/visualScreenScopes'
import { getRaidPlanLinkInfo } from '../components/raidPlanLink'
import {
  AssignmentViewMode,
  resolveAssignmentViewMode,
} from '../components/assignmentTimeline'
import {
  AssignmentPhaseTimestampBadgeData,
  buildAssignmentPhaseTimestampBadgeData,
  PhaseDivider,
} from '../components/AssignmentPhaseTimestampBadge'

type SpecializationInfo = {
  name: string
  icon: string
  classId: number
  className: string
}

type Options = {
  owner: RaidPlanOwner
  ability: RaidPlanEncounterAssignmentAbility
  scopes: { id: string; castGroups: number[][] }[]
  entitiesByScopeId: Record<string, RaidPlanAssignmentEntity[]>
  entries: { time: number; phaseIndex: number }[]
  phaseDividers: PhaseDivider[]
  specializationById: Record<number, SpecializationInfo>
  memberInfoByCharacterId: Record<number, { name: string; className: string }>
  raidPlanPlayers: RaidstratsPlayer[]
  ownCharacterIds?: Set<number>
  overriddenEntityIds?: Set<string>
  getEntityLabel?: (entity: RaidPlanAssignmentEntity) => ReactNode
  assignCharacterIcons?: boolean
  preloadedVisual?: VisualPlan | null
  loadVisual?: boolean
}

const useAbilityVisualPlanData = ({
  owner,
  ability,
  scopes,
  entitiesByScopeId,
  entries,
  phaseDividers,
  specializationById,
  memberInfoByCharacterId,
  raidPlanPlayers,
  ownCharacterIds,
  overriddenEntityIds,
  getEntityLabel,
  assignCharacterIcons = false,
  preloadedVisual,
  loadVisual = true,
}: Options): {
  visualPlan: VisualPlanData | null
  visualRaidPlan: VisualRaidPlan | null
} => {
  const { data: fetchedVisual } = useVisualPlan(
    owner,
    loadVisual ? ability.visual_id : undefined,
  )
  useVisualPlanLiveSync({
    owner: loadVisual ? owner : undefined,
    visualId: loadVisual ? ability.visual_id : undefined,
  })
  const linkedVisual = loadVisual ? fetchedVisual : preloadedVisual ?? undefined
  const abilityViewMode: AssignmentViewMode = resolveAssignmentViewMode(
    ability.view_mode,
    entries.length,
    ability.assignment_type,
    ability.nsrt_interrupt_module,
    ability.nsrt_transition_module,
  )
  const activeVisualScreens = linkedVisual
    ? getActiveVisualScreens(linkedVisual.screens, ability.visual_screen_ids)
    : []
  const scopeFillByScreenId = new Map<
    string,
    { scopeId: string; entities: RaidPlanAssignmentEntity[] }
  >()
  const screenCastBadges = new Map<
    string,
    AssignmentPhaseTimestampBadgeData[]
  >()
  scopes.forEach((scope, scopeIndex) => {
    const flatScopeEntities = entitiesByScopeId[scope.id] ?? []
    const scopeScreens = getScopeScreens({
      viewMode: abilityViewMode,
      scopeId: scope.id,
      scopeIndex,
      activeScreens: activeVisualScreens,
    })
    scopeScreens.forEach(({ screen }) => {
      if (!scopeFillByScreenId.has(screen.id)) {
        scopeFillByScreenId.set(screen.id, {
          scopeId: scope.id,
          entities: flatScopeEntities,
        })
      }
    })
    if (scopeScreens.length > 0) {
      scope.castGroups.forEach((castGroup, groupIndex) => {
        const numbered = scopeScreens[groupIndex % scopeScreens.length]
        if (!numbered) return
        const badge = buildAssignmentPhaseTimestampBadgeData({
          castIndexes: castGroup,
          entries,
          phaseDividers,
        })
        const existing = screenCastBadges.get(numbered.screen.id) ?? []
        existing.push(badge)
        screenCastBadges.set(numbered.screen.id, existing)
      })
    }
  })
  const combinedVisualScreens: VisualPlanScreen[] = activeVisualScreens.flatMap(
    ({ screen, number }) => {
      const fill = scopeFillByScreenId.get(screen.id)
      if (!fill) return []
      const entityAssignments = resolveVisualPlanEntityAssignments({
        shapes: screen.shapes,
        entities: fill.entities,
        specializationById,
        memberInfoByCharacterId,
        matchMode: 'number-first',
      })
      entityAssignments.forEach((badge) => {
        const entity = fill.entities[badge.index]
        if (!entity) return
        if (getEntityLabel) badge.label = getEntityLabel(entity)
        if (entity.id && overriddenEntityIds?.has(entity.id)) {
          badge.isOverridden = true
        }
        if (
          ownCharacterIds &&
          entity.kind === 'member' &&
          ownCharacterIds.has(entity.character_id)
        ) {
          badge.isOwn = true
        }
        if (assignCharacterIcons && entity.kind === 'member') {
          badge.assignedSpecId = entity.spec_id ?? undefined
          badge.assignedClassName =
            (entity.spec_id
              ? specializationById[entity.spec_id]?.className
              : undefined) ||
            memberInfoByCharacterId[entity.character_id]?.className
        }
      })
      return [
        {
          key: `${fill.scopeId}:${screen.id}`,
          scopeId: fill.scopeId,
          screen,
          number,
          entityAssignments,
          castTimestamps: screenCastBadges.get(screen.id) ?? [],
        },
      ]
    },
  )
  const visualPlan: VisualPlanData | null =
    linkedVisual && combinedVisualScreens.length > 0
      ? {
          encounterId: linkedVisual.encounterId,
          screens: combinedVisualScreens,
          showScreenNames: entries.length <= 1,
        }
      : null
  const linkInfo = getRaidPlanLinkInfo(ability.raid_plan_link)
  const visualRaidPlan: VisualRaidPlan | null =
    linkInfo.hasValidRaidPlanId && linkInfo.extractedRaidPlanId
      ? {
          raidPlanId: linkInfo.extractedRaidPlanId,
          players: raidPlanPlayers,
        }
      : null
  return { visualPlan, visualRaidPlan }
}

export default useAbilityVisualPlanData
